<script setup>
// Addendum §4 Pagination.
//   mode="cursor"  — for anything the user MUTATES IN PLACE (approval queue,
//                    timesheet lists, audit log). Prev/Next (or Load more) walk
//                    a keyset cursor; LIMIT/OFFSET would silently skip rows as
//                    the list shifts under mutation. A total count still ships
//                    (a separate COUNT) because it's load-bearing for
//                    reconciliation.
//   mode="offset"  — for read-only, snapshot-consistent surfaces (reports).
// The component is presentational; the host owns the cursor/offset. Numerals
// are tabular (§5).
import { computed } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	mode: { type: String, default: 'offset' }, // offset | cursor
	page: { type: Number, default: 1 },
	pageCount: { type: Number, default: 1 }, // offset mode
	total: { type: Number, default: null },
	hasPrev: { type: Boolean, default: false }, // cursor mode
	hasNext: { type: Boolean, default: false }, // cursor mode
	noun: { type: String, default: 'result' },
})
const emit = defineEmits(['prev', 'next', 'load-more', 'go'])

const canPrev = computed(() => (props.mode === 'cursor' ? props.hasPrev : props.page > 1))
const canNext = computed(() => (props.mode === 'cursor' ? props.hasNext : props.page < props.pageCount))
const plural = (n) => (n === 1 ? props.noun : props.noun + 's')
</script>

<template>
	<div class="l-pagination">
		<div class="count">
			<template v-if="total != null">
				<span class="num n">{{ total }}</span> {{ plural(total) }}
			</template>
		</div>

		<!-- cursor: Load more, or Prev/Next -->
		<div v-if="mode === 'cursor'" class="controls">
			<button type="button" class="pbtn" :disabled="!canPrev" @click="emit('prev')">
				<LIcon name="chevron-left" :size="13" /> Newer
			</button>
			<button type="button" class="pbtn" :disabled="!canNext" @click="emit('next')">
				Older
				<LIcon name="chevron-right" :size="13" />
			</button>
		</div>

		<!-- offset: numbered -->
		<div v-else class="controls">
			<button type="button" class="pbtn" :disabled="!canPrev" @click="emit('prev')" aria-label="Previous page">
				<LIcon name="chevron-left" :size="13" />
			</button>
			<span class="pageinfo num">Page {{ page }} of {{ pageCount }}</span>
			<button type="button" class="pbtn" :disabled="!canNext" @click="emit('next')" aria-label="Next page">
				<LIcon name="chevron-right" :size="13" />
			</button>
		</div>
	</div>
</template>

<style scoped>
.l-pagination
{
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sp-4);
	padding: var(--sp-3) 0;
}

.count
{
	font-size: var(--text-sm);
	color: var(--text-3);
}

.count .n
{
	font-weight: 600;
	color: var(--text-2);
}

.controls
{
	display: inline-flex;
	align-items: center;
	gap: var(--sp-2);
}

.pbtn
{
	display: inline-flex;
	align-items: center;
	gap: 5px;
	font-family: var(--font-ui);
	font-size: var(--text-chip);
	font-weight: 600;
	color: var(--text-2);
	background: var(--surface);
	border: 1px solid var(--line);
	border-radius: var(--r-sm);
	padding: 6px 12px;
	min-height: var(--target-min);
	cursor: pointer;
}

.pbtn:hover:not(:disabled)
{
	background: var(--surface-sunken);
	color: var(--text);
}

.pbtn:disabled
{
	opacity: 0.45;
	cursor: not-allowed;
}

.pageinfo
{
	font-size: var(--text-sm);
	color: var(--text-2);
	padding: 0 var(--sp-2);
}
</style>
